import { useState } from "react";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/data/images";
import { PageHero } from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";

const inputClass =
  "w-full border border-black/15 bg-white px-4 py-3.5 text-[15px] text-black outline-none transition-colors duration-300 placeholder:text-[var(--ink-3)] focus:border-black";

const Field = ({ id, label, type = "text", value, onChange, textarea, required, children }) => (
  <div>
    <label htmlFor={id} className="mb-2.5 block text-[13px] font-medium tracking-tight text-black">
      {label}{required && <span className="text-[var(--ink-3)]"> *</span>}
    </label>
    {children ? children : textarea ? (
      <textarea id={id} data-testid={`contact-${id}`} value={value} onChange={onChange} required={required} rows={6} className={`${inputClass} resize-none`} />
    ) : (
      <input id={id} data-testid={`contact-${id}`} type={type} value={value} onChange={onChange} required={required} className={inputClass} />
    )}
  </div>
);

export default function Contact() {
  const { content, api } = useLang();
  const { ui, footer } = content;
  const [form, setForm] = useState({ last_name: "", first_name: "", email: "", organization: "", subject: footer.emails[0].label, message: "" });
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try { await axios.post(`${api}/contact`, form); setState("sent"); } catch { setState("error"); }
  };

  return (
    <>
      <PageHero label={ui.contact} image={IMAGES.contact} compact />
      <section className="bg-white" data-testid="contact-section">
        <div className="container-x pt-8 pb-20 md:pt-10 md:pb-28 grid grid-cols-12 gap-x-12 gap-y-14">
          <FadeUp className="col-span-12 lg:col-span-4">
            <div className="earmark mb-7 text-black">{ui.emailLabel}</div>
            <ul className="space-y-7" data-testid="contact-email-list">
              {footer.emails.map((e) => (
                <li key={e.address}>
                  <div className="text-[13px] text-[var(--ink-3)]">{e.label}</div>
                  <a href={`mailto:${e.address}`} data-testid={`contact-email-${e.address.split("@")[0]}`} className="mt-1 block font-head text-lg text-black transition-colors hover:text-[var(--ink-2)]">{e.address}</a>
                </li>
              ))}
            </ul>
          </FadeUp>

          <FadeUp delay={0.15} className="col-span-12 lg:col-span-7 lg:col-start-6">
            {state === "sent" ? (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="contact-success" className="border border-[var(--line)] bg-[var(--bg-grey)] p-10">
                <p className="font-head text-2xl text-black">{ui.sent}</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form">
                <div className="space-y-6">
                  <Field id="last_name" label={ui.lastName} value={form.last_name} onChange={set("last_name")} required />
                  <Field id="first_name" label={ui.firstName} value={form.first_name} onChange={set("first_name")} required />
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Field id="email" label={ui.email} type="email" value={form.email} onChange={set("email")} required />
                    <Field id="organization" label={ui.organization} value={form.organization} onChange={set("organization")} />
                  </div>
                  <Field id="subject" label={ui.subject} required>
                    <select id="subject" data-testid="contact-subject" value={form.subject} onChange={set("subject")} className={`${inputClass} appearance-none`}>
                      {footer.emails.map((e) => <option key={e.address} value={e.label}>{e.label}</option>)}
                    </select>
                  </Field>
                  <Field id="message" label={ui.message} value={form.message} onChange={set("message")} textarea required />
                </div>
                <div className="mt-9 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                  <p className="max-w-sm text-[12px] leading-relaxed text-[var(--ink-3)]">{ui.formNote}</p>
                  <button type="submit" data-testid="contact-submit" disabled={state === "sending"} className="btn-dark shrink-0 disabled:opacity-60">{ui.send} <ArrowRight size={16} /></button>
                </div>
                {state === "error" && <p className="mt-5 text-sm text-red-600" data-testid="contact-error">{ui.sendError}</p>}
              </form>
            )}
          </FadeUp>
        </div>
      </section>
    </>
  );
}
